import React, { useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Eye, Search } from 'lucide-react';
import useTrackingTime from "@/hooks/trackingTime";
import TableCustomer from '@/components/table';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const ListTracking: React.FC = () => {
    const { data, isLoading, query, handleSetQuery } = useTrackingTime();
    const navigate = useNavigate();

    const { register, handleSubmit } = useForm({
        defaultValues: {
            uid: query.uid || ''
        }
    });

    // Memoize columns to prevent unnecessary re-renders
    const columns = useMemo(() => [
        {
            value: 'user.avatarUrl',
            label: 'Avatar',
            isImage: true,
            noRound: false
        },
        {
            value: 'user.name',
            label: 'Name'
        },
        {
            value: 'user.isWorking',
            label: 'Is working',
            isStatus: true,
            center: true
        },
        {
            value: 'monitoring.projectName',
            label: 'Project'
        },
        {
            value: 'monitoring.taskName',
            label: 'Task'
        },
        {
            value: 'monitoring.totalTime',
            label: 'Total Time',
        },
        {
            value: 'view',
            label: 'View',
            render: ({ data }: { data: any }) => {
                return (
                    <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={() => navigate(`/tracking-time/${data?.user?._id}`)}
                    >
                        <Eye />
                    </Button>
                )
            }
        }
    ], [navigate]);

    const onSubmit: SubmitHandler<FieldValues> = useCallback((data) => {
        handleSetQuery({ uid: data.uid || '', page: 1 }); // Reset to page 1 when searching
    }, [handleSetQuery]);

    const handlePageSizeChange = useCallback((value: string) => {
        handleSetQuery({ limit: Number(value), page: 1 }); // Reset to page 1 when changing page size
    }, [handleSetQuery]);

    const handlePageChange = useCallback((page: number) => {
        handleSetQuery({ ...query, page });
    }, [handleSetQuery, query]);

    // Memoize pagination object
    const pagination = useMemo(() => ({
        currentPage: query.page || 1,
        totalPages: Math.ceil((data?.total || 0) / (query.limit || 10)),
        totalItems: data?.total || 0,
        itemsPerPage: query.limit || 10,
        onPageChange: handlePageChange
    }), [query.page, query.limit, data?.total, handlePageChange]);

    return (
        <div className="container mx-auto p-2 sm:p-6 space-y-4 sm:space-y-6">
            <Card>
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
                    <div>
                        <CardTitle className="text-xl sm:text-2xl">Tracking Time</CardTitle>
                        <p className="text-sm sm:text-base text-muted-foreground">
                            Monitor user activities and time tracking
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                        <div className="relative w-full sm:w-auto">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className='flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto'
                            >
                                <Input
                                    placeholder="Search by user"
                                    className="pl-10 w-full sm:w-[200px]"
                                    {...register('uid')}
                                />
                                <Button type="submit" className="w-full sm:w-auto">Search</Button>
                            </form>
                        </div>
                        <Select
                            value={query.limit?.toString()}
                            onValueChange={handlePageSizeChange}
                        >
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <SelectValue placeholder="Items per page" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="10">10 Items</SelectItem>
                                <SelectItem value="25">25 Items</SelectItem>
                                <SelectItem value="50">50 Items</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        {data?.data && data.data.length > 0 ? (
                            <TableCustomer
                                columns={columns}
                                data={data.data}
                                loading={isLoading}
                                pagination={pagination}
                            />
                        ) : (
                            <div className="flex justify-center items-center h-[200px]">
                                <p className="text-muted-foreground text-sm sm:text-base">
                                    {isLoading ? 'Loading tracking data...' : 'No tracking data found'}
                                </p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Summary Card */}
            {/* <Card>
                <CardHeader>
                    <CardTitle>Time Tracking Summary</CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-3 gap-4">
                    <div className="border rounded-lg p-4 text-center">
                        <Clock className="mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium">Total Tracked Time</p>
                        <p className="text-2xl font-bold">
                            {data?.data ?
                                formatTotalTime(
                                    data.data.reduce((sum, item) => sum + item.monitoring.totalTime, 0)
                                ) :
                                '0h 0m'
                            }
                        </p>
                    </div>
                    <div className="border rounded-lg p-4 text-center">
                        <Filter className="mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium">Total Users Tracking</p>
                        <p className="text-2xl font-bold">{data?.data.reduce((sum, item) => sum + (item.user.isWorking ? 1 : 0), 0) || 0}</p>
                    </div>
                    <div className="border rounded-lg p-4 text-center">
                        <Filter className="mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium">Active Projects</p>
                        <p className="text-2xl font-bold">
                            {data?.data ?
                                new Set(data.data.map(item => item.monitoring.projectName)).size :
                                0
                            }
                        </p>
                    </div>
                </CardContent>
            </Card> */}
        </div>
    );
};

export default React.memo(ListTracking);